import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SignupSetUsernameAndBio.css';
import TopBar from '../../common/TopBar/TopBar';
import AuthInput from '../../buttons/AuthInput';
import CTAButton from '../../buttons/CTAButton';
import { emailSignup } from '../../../api/auth';
import VIToast from '../../common/VIToast/VIToast';
import welcomeImg from '../../../assets/img/img_welcome.png';
import vividLogo from '../../../assets/img/vivid_logo.svg';
import aiGenerateIcon from '../../../assets/icons/icon_ai_generate.svg';
import VIModal from '../../common/VIModal/VIModal';

const SignupSetUsernameAndBio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, password } = location.state || {};

  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showNoImageModal, setShowNoImageModal] = useState(false);
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    setIsFormValid(username.length > 0 && bio.length > 0);
  }, [username, bio]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Check file size first
      if (file.size > MAX_FILE_SIZE) {
        setToastMessage('Image size should be less than 10MB');
        setShowToast(true);
        return;
      }

      // Create preview URL using URL.createObjectURL instead of FileReader
      try {
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        setProfileImage(file);

        // Clean up the object URL when preview is set
        return () => URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Error handling image upload:', error);
        setToastMessage('Failed to process image. Please try again.');
        setShowToast(true);
      }
    }
  };

  const handleSignup = async () => {
    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('username', username);
      formData.append('bio', bio);
      if (profileImage) {
        formData.append('profile_picture', profileImage);
      }

      await emailSignup(formData);
      setToastMessage('Sign up was a success!');
      setShowToast(true);
      setTimeout(() => {
        navigate('/playground');
      }, 1000);
    } catch (error: any) {
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.response) {
        switch (error.response.status) {
          case 409:
            errorMessage = 'This username is already taken';
            break;
          case 400:
            errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          'Invalid input. Please check your information.';
            break;
          case 413:
            errorMessage = 'Profile image is too large. Please choose a smaller image.';
            break;
          default:
            errorMessage = error.response.data?.message || 
                          'An error occurred during sign up. Please try again.';
        }
      }
      
      setToastMessage(errorMessage);
      setShowToast(true);
      console.error('Signup failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNext = async () => {
    if (!isFormValid || isUpdating) return;

    if (!profileImage) {
      setShowNoImageModal(true);
      return;
    }

    await handleSignup();
  };

  const handleProceedWithoutImage = () => {
    setShowNoImageModal(false);
    handleSignup();
  };

  const handleCancelNoImage = () => {
    setShowNoImageModal(false);
  };

  return (
    <div className="signup-username-bio">
      {toastMessage && (
        <VIToast
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
        />
      )}
      <TopBar showBackButton={true} />
      
      <div className="profile-image-section">
        <h2 className="welcome-text">Welcome!</h2>
        <p className="welcome-description">Write up a quick intro about yourself!</p>
        
        <div className="profile-image-wrapper">
          <label htmlFor="profile-image-input" className="profile-image-container">
            {previewUrl ? (
              <img src={previewUrl} alt="Profile" className="profile-image" />
            ) : (
              <img src={vividLogo} alt="Default" className="default-logo" />
            )}
          </label>
          <img src={welcomeImg} alt="Welcome" className="welcome-icon" />
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="image-upload"
            id="profile-image-input"
          />
        </div>
      </div>

      <div className="input-container">
        <AuthInput
          type="text"
          placeholder="Username"
          value={username}
          onChange={(value: string) => setUsername(value)}
        />
        <AuthInput
          type="text"
          placeholder="Bio - Tell us how Vivid you are!"
          value={bio}
          onChange={(value: string) => setBio(value)}
        />
      </div>
      

      
      <CTAButton
        text={isUpdating ? "Please wait..." : "Complete"}
        onClick={handleNext}
        isActive={isFormValid && !isUpdating}
      />

      <VIModal
        isOpen={showNoImageModal}
        onClose={handleCancelNoImage}
        title="No Profile Picture"
      >
        <div className="no-image-modal-content">
          <p>Are you sure you want to proceed without setting a profile picture?</p>
          <div className="modal-buttons">
            <button 
              className="modal-button secondary" 
              onClick={handleCancelNoImage}
            >
              Cancel
            </button>
            <button 
              className="modal-button primary" 
              onClick={handleProceedWithoutImage}
            >
              Proceed
            </button>
          </div>
        </div>
      </VIModal>
    </div>
  );
};

export default SignupSetUsernameAndBio; 